import type { Invoice } from "@/lib/invoice.api";
import { lineSummary } from "@/pages/documents/components/DocumentWorkspace";
import {
  PAYMENT_LABELS,
  STATUS_LABELS,
  type InvoicePaymentFilter,
  type InvoiceStatusFilter,
} from "./invoice.constants";
import { getInvoiceLabel } from "./invoice-formatters";

export interface InvoiceMetrics {
  totalValue: number;
  openCount: number;
  overdueCount: number;
}

export function isInvoiceOverdue(invoice: Invoice): boolean {
  return Boolean(
    invoice.status === "issued" &&
      invoice.paymentStatus !== "paid" &&
      invoice.dueDate &&
      new Date(invoice.dueDate).getTime() < Date.now(),
  );
}

export function sortInvoicesByNewest(invoices: Invoice[]): Invoice[] {
  return [...invoices].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
  );
}

export function getInvoiceMetrics(invoices: Invoice[]): InvoiceMetrics {
  return {
    totalValue: invoices.reduce((sum, invoice) => sum + parseFloat(invoice.totalAmount), 0),
    openCount: invoices.filter(
      (invoice) => invoice.paymentStatus !== "paid" && invoice.status === "issued",
    ).length,
    overdueCount: invoices.filter(isInvoiceOverdue).length,
  };
}

export interface FilteredInvoiceTotals {
  count: number;
  invoiced: number;
  paid: number;
  remaining: number;
}

// Money totals only consider issued invoices: drafts/cancelled/expired carry no receivable.
export function getFilteredInvoiceTotals(invoices: Invoice[]): FilteredInvoiceTotals {
  const issued = invoices.filter((invoice) => invoice.status === "issued");
  const invoiced = issued.reduce((sum, i) => sum + parseFloat(i.totalAmount), 0);
  const paid = issued.reduce((sum, i) => sum + parseFloat(i.paidAmount ?? "0"), 0);
  const remaining = issued.reduce(
    (sum, i) => sum + Math.max(parseFloat(i.totalAmount) - parseFloat(i.paidAmount ?? "0"), 0),
    0,
  );
  return { count: issued.length, invoiced, paid, remaining };
}

export function filterInvoices(
  invoices: Invoice[],
  search: string,
  statusFilter: InvoiceStatusFilter,
  paymentFilter: InvoicePaymentFilter,
): Invoice[] {
  const needle = search.trim().toLowerCase();

  return invoices.filter((invoice) => {
    const matchesStatus = statusFilter === "all" || invoice.status === statusFilter;
    const matchesPayment = paymentFilter === "all" || invoice.paymentStatus === paymentFilter;
    if (!matchesStatus || !matchesPayment) return false;
    if (!needle) return true;

    return [
      getInvoiceLabel(invoice),
      STATUS_LABELS[invoice.status],
      PAYMENT_LABELS[invoice.paymentStatus],
      invoice.partySnapshot?.fullName,
      invoice.partySnapshot?.phone,
      invoice.partySnapshot?.email,
      lineSummary(invoice.lines),
      ...invoice.lines.flatMap((line) => [
        line.description,
        line.lineType,
        line.ticketDetails?.passengerName,
        line.shopDetails?.passengerName,
      ]),
    ]
      .filter(Boolean)
      .some((value) => String(value).toLowerCase().includes(needle));
  });
}
